import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { IPortfolioPerformanceAccounts } from '../portfolio-performance.types';
import { TransactionDataType, TransactionsContextValue } from './transactions.types';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import Utils from '../../../utils/immersive_utils';
import { useSmartInactivityWake } from '../../../hooks/use-smart-inactivity-wake';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10min

export const TransactionsContext = createContext<TransactionsContextValue>({} as any);

export const TransactionsProvider: React.FC<{
  account: IPortfolioPerformanceAccounts;
  children: React.ReactNode;
}> = ({ account, children }) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const [data, setData] = useState<TransactionDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const intervalRef = useRef<number | null>(null);
  const isFirstLoad = useRef<boolean>(true);

  const getTransactions = useCallback(() => {
    const adjustedEndDate = new Date();
    const adjustedBeginDate = new Date(adjustedEndDate.getTime() - 24 * 60 * 60 * 1000);

    const nameId = account.name.replace(/\s+/g, '_').toUpperCase();

    const params = {
      jsonrpc: '2.0',
      id: `PP_TRANSACTIONS_${nameId}`,
      method: account.exchange === 'deribit' ? 'load_deribit_transactions' : 'okx_transactions',
      params: {
        credentials: { name: account.name },
        begin: adjustedBeginDate,
        end: adjustedEndDate,
      },
    };

    sendMessage(JSON.stringify(params));
    subscribeToChannel(`PP_TRANSACTIONS_${nameId}`, (response: any) => {
      setLoading(false);
      if (!response) return;

      const transactions = Utils.csvToObject(response.result) as TransactionDataType[];
      setData(transactions);
    });
  }, [account, sendMessage, subscribeToChannel]);

  const startPolling = useCallback(() => {
    if (isFirstLoad.current) {
      setLoading(true);
      getTransactions();
      isFirstLoad.current = false;
    }

    const id = window.setInterval(() => {
      getTransactions();
    }, 10_000);

    intervalRef.current = id;
  }, [getTransactions]);

  useEffect(() => {
    startPolling();

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startPolling]);

  useSmartInactivityWake(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setData([]);
    isFirstLoad.current = true;

    startPolling();
  }, INACTIVITY_TIMEOUT);

  return (
    <TransactionsContext.Provider
      value={{
        account,
        data,
        setData,
        loading,
        setLoading,
      }}
    >
      {children}
    </TransactionsContext.Provider>
  );
};
